import { Fetch } from '@inface-sdk/common'
import cookie from 'js-cookie'
import GLOBAL_VARIABLE from "../constants/global-variable.enum"
import InfaceAuthError from '../inface-auth/inface-auth-error.class'

const IAS_PATH = {
  EXPIRE_GUEST_SESSION: 'expire/guest-web-token',
  ISSUE_GUEST_SESSION_BY_WEB_TICKET: 'issue/guest-web-token/by-web-ticket',
  VERIFY_GUEST_SESSION: 'verify/guest-web-token',
}

/**
 * @param {Object} conf
 * @param {String} conf.gid Required
 * @param {String} conf.ticket
 * @param {String} conf.iasApiUrl
 * @param {Boolean} conf.useGuestSession
 * @param {Number} conf.tokenRefreshInterval
 * @param {InfaceUser} conf.user Required
 */
export default class GuestService {
  constructor(conf = {}) {
    const authType = document.querySelector('meta[name="gamescale-sdk"][content="web-auth"]')?.getAttribute('data-auth-type')
    
    this.gid = conf.gid
    this.ticket = conf.ticket
    this.iasApiUrl = conf.iasApiUrl
    this.useGuestSession = (authType === 'guest' || conf.useGuestSession) ?? false // meta tag 가 최우선

    this.user = conf.user
    this.tokenRefreshInterval = conf.tokenRefreshInterval

    this._objGuestSessionTimeout = null
  }

  get guestWebTokenKeyWithGid() {
    return `${GLOBAL_VARIABLE.GUEST_WEB_TOKEN_KEY}_${this.gid}`
  }

  get guestWebToken() {
    return cookie.get(this.guestWebTokenKeyWithGid) || ''
  }

  set guestWebToken(value) {
    cookie.set(this.guestWebTokenKeyWithGid, value, { domain: GLOBAL_VARIABLE.WEB_TOKEN_DOMAIN })
  }

  get hasGuestSession() {
    return this.useGuestSession && !!this.guestWebToken
  }

  async init() {
    console.log('[web-auth] GuestService.init()', this.useGuestSession)
    if (!this.useGuestSession) return

    if (this.ticket) {
      await this.#processSignIn({ ticket: this.ticket })
      return this.#redirectWithoutSearchParamsAndHistory()
    }

    await this.#checkAndValidateGuestSession()
  }

  isSignedIn() {
    if (!this.useGuestSession) return false
    return !!this.guestWebToken
  }

  async processSignOut() {
    if (!this.hasGuestSession) return { success: false }

    await this.#expireGuestSession()

    return { success: true }
  }

  async processVerifyGuestSession() {
    if (!this.hasGuestSession) return { success: false }

    return await this.#verifyGuestSession()
  }

  #redirectWithoutSearchParamsAndHistory() {
    const url = new URL(window.location.href)
    url.searchParams.delete('signed_in_by')
    url.searchParams.delete('web_ticket')
    url.searchParams.delete('ticket')
    window.location.replace(url.toString())
  }


  #setupAndRefreshGuestSession(data) {
    this.guestWebToken = data.web_token
    this.user.setProfile(data)

    this.#refreshGuestSession()
  }

  async #processSignIn({ ticket, gid = this.gid }) {
    if (!this.useGuestSession) return { success: false }
    if (!ticket || !gid) {
      console.error('processSignIn: ticket or gid is not provided.', 'ticket: ', ticket, 'gid: ', gid)
      return { success: false }
    }

    const { success, data, error } = await this.#issueGuestSessionByWebTicket({ ticket, gid })
    if (!success) return { success, error }

    this.#setupAndRefreshGuestSession(data)

    return { success: true, data }
  }

  async #checkAndValidateGuestSession() {
    if (!this.hasGuestSession) return

    const { success, data, error } = await this.#verifyGuestSession()
    if (!success) return { success, error }

    this.#setupAndRefreshGuestSession(data)
  }

  #refreshGuestSession() {
    if (!this.hasGuestSession) return

    const interval = this.tokenRefreshInterval
    const max = 600000 // 10 분
    const min = 60000  // 1분

    if (interval < max) return

    // 1~10 분 가변
    const diff = Math.floor(Math.random() * (max - min + 1)) + min

    if (this._objGuestSessionTimeout) {
      clearTimeout(this._objGuestSessionTimeout)
    }
    
    this._objGuestSessionTimeout = setTimeout(async () => {
      try {
        const { success, data } = await this.#verifyGuestSession()
        if (!success) {
          clearTimeout(this._objGuestSessionTimeout)
          window.location.reload()
          return
        }
    
        this.#setupAndRefreshGuestSession(data)
      } catch (error) {
        console.error('Error during guest session refresh:', error)
        clearTimeout(this._objGuestSessionTimeout)
        window.location.reload()
      }
    }, interval - diff)
  }

  clearSession() {
    this.guestWebToken = ''
    cookie.remove(GLOBAL_VARIABLE.GUEST_WEB_TOKEN_KEY, { domain: GLOBAL_VARIABLE.WEB_TOKEN_DOMAIN }) // 기존 키 제거
    cookie.remove(this.guestWebTokenKeyWithGid, { domain: GLOBAL_VARIABLE.WEB_TOKEN_DOMAIN })
    this.user.setProfile({})
  }

  async #expireGuestSession() {
    try {
      const url = `${this.iasApiUrl}/${IAS_PATH.EXPIRE_GUEST_SESSION}`
      const response = await Fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'x-ias-web-token': this.guestWebToken,
        },
      })

      if (!response.ok) {
        throw response
      }
      
      this.clearSession()
      return { success: true }
    } catch(e) {
      this.clearSession()
      const errorData = typeof e.json === "function" ? await e.json() : e
      const error = new InfaceAuthError({ code: 113122, response: e, data: errorData }) // 만료 실패
      console.error('expireGuestSession failed', error.getMessageWithCode())
      return { success: false, error }
    }
  }

  async #issueGuestSessionByWebTicket({ ticket, gid }) {
    try {
      const url = `${this.iasApiUrl}/${IAS_PATH.ISSUE_GUEST_SESSION_BY_WEB_TICKET}`
      const body = {
        web_ticket: ticket,
        gid,
        domain: window.location.hostname,
      }

      const response = await Fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      })

      if (!response.ok) {
        throw response
      }

      // { web_token, platform_type, platform_user_id }
      const data = await response.json()
      if (data?.web_token) {
        return { success: true, data }
      }
      
      return { success: false, error: data }
    } catch(e) {
      const errorData = typeof e.json === "function" ? await e.json() : e
      if (errorData.error?.name === 'IAS_INVALID_REQUEST') { // 넥슨 계정 링킹한 스팀계정으로  guest 로그인 시도 시 발생
        const error = new InfaceAuthError({ code: 113125, response: e, data: errorData }) // 발급 실패
        console.error('issueGuestSessionByWebTicket failed', error.getMessageWithCode())
        alert(error.getMessageWithCode())
        return { success: false, error }
      }
      const error = new InfaceAuthError({ code: 113124, response: e, data: errorData }) // 발급 실패
      console.error('issueGuestSessionByWebTicket failed', error.getMessageWithCode())
      return { success: false, error }
    }
  }

  async #verifyGuestSession() {
    try {
      // TODO throttling 적용 필요
      const url = `${this.iasApiUrl}/${IAS_PATH.VERIFY_GUEST_SESSION}`
      const response = await Fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'x-ias-web-token': this.guestWebToken,
        },
      })

      if (!response.ok) {
        throw response
      }

      // { web_token, platform_type, platform_user_id }
      const data = await response.json()
      if (data?.web_token) {
        return { success: true, data }
      }
      
      return { success: false, error: data }
    } catch(e) {
      await this.processSignOut()

      const errorData = typeof e.json === "function" ? await e.json() : e
      const error = new InfaceAuthError({ code: 113123, response: e, data: errorData }) // 검증 실패
      console.error('verifyGuestSession failed', error.getMessageWithCode())
      return { success: false, error }
    }
  }
}